'use client';

/**
 * Background flusher for pending grid-metadata writes that the launch
 * wizard couldn't deliver inline.
 *
 * `useLaunchGrid` stashes the wizard's payload into `localStorage` via
 * `setPendingMetadata` BEFORE the metadata POST so a transient failure
 * (expired Privy token, indexer 503, network blip, browser crash mid-
 * navigation) doesn't lose the user's plinth / skybox / name picks.
 * This hook is mounted by `LobbyClient` and walks the pending map on
 * mount + when `viewerAddress` flips to a connected state, retrying
 * each entry with the *current* access token.
 *
 * Why a hook (and not a page/route handler):
 *
 *  - Retries need a fresh access token, which is a client-only Privy
 *    primitive (`useWallet().getAccessToken`).
 *  - Multiple boards may be pending if the user launched a few in
 *    quick succession; we want to flush all of them, not just the
 *    currently-active board, so a sweep on mount is the right shape.
 *  - Pending entries naturally clear themselves on a successful POST,
 *    so the next idle render is a no-op.
 *
 * Failure handling: each retry has its own internal backoff (in
 * `postMetadata`). If retries still fail (e.g. the user reconnected
 * with a *different* wallet that doesn't own the grid), the entry
 * stays in localStorage and ages out after 24h via `MAX_AGE_MS`. We
 * don't surface a UI toast here — the user already saw a wizard
 * warning, and the lobby will keep trying silently in the background.
 */

import { useEffect, useRef, useState } from 'react';

import { useWallet } from '@/hooks/use-wallet';

import {
  clearPendingMetadata,
  listPendingMetadata,
  type PendingMetadataPayload,
} from './pending-metadata';
import { postMetadata } from './launch-metadata';

interface FlushOptions {
  /** When set, only flush the entry matching this compositeGridId.
   *  Used by callers that want to opportunistically retry only the
   *  active board (e.g. a "Saving metadata…" toast tied to the board
   *  page mount). Omit to sweep every pending entry. */
  onlyForCompositeGridId?: string;
}

/**
 * Reactive view of the targeted-flush state for a single
 * `compositeGridId`. The lobby reads this to render a "saving
 * metadata… keep this tab open" banner so creators don't close the
 * tab during the retry window.
 *
 * `null` is returned when the hook is called without
 * `onlyForCompositeGridId` (the global sweep path) — there's no
 * per-board state to surface in that mode.
 */
export interface PendingMetadataFlushState {
  /** True when there is a pending entry in `localStorage` for the
   *  targeted compositeGridId. Goes back to `false` immediately on
   *  successful flush (the entry is cleared) or once the entry ages
   *  past `MAX_AGE_MS` and is GC'd by `listPendingMetadata`. */
  isPending: boolean;
  /** True while a flush attempt is in-flight for the targeted
   *  compositeGridId. Used by the banner to swap copy from "queued"
   *  to "retrying" so the UI doesn't look frozen during the POST. */
  isFlushing: boolean;
}

async function flushOne(
  entry: PendingMetadataPayload,
  accessToken: string | null,
): Promise<void> {
  const result = await postMetadata({
    managerAddress: entry.managerAddress,
    numericId: entry.numericId,
    body: entry.body,
    accessToken,
  });
  if (result.ok) {
    clearPendingMetadata(entry.compositeGridId);
    return;
  }
  // We previously deleted the entry on a 403 "Only the grid creator"
  // assuming a different wallet was connected. In practice that
  // assumption is wrong immediately after launch: Privy's
  // `linkedAccounts` view of the signing wallet can lag the on-chain
  // `creator_id` by several seconds (especially for 7702-delegated
  // EOAs and freshly-attached smart wallets), so the very first POST
  // can 403 even though the *correct* user is connected. Permanently
  // dropping the entry there destroys the only persisted copy of the
  // user's name / image / theming picks.
  //
  // Treat every non-2xx (404 indexer-lag, 5xx, 401, 403) the same:
  // leave the entry in place so the next sweep — triggered by board
  // navigation, wallet reconnect, or the lobby remount — can retry
  // with a fresher Privy session. The 24h `MAX_AGE_MS` floor in
  // `pending-metadata.ts` prevents stale entries from accumulating
  // indefinitely if the wallet that owns the grid never reconnects.
}

/**
 * Mount-time sweep: walk every pending entry and try to flush each
 * with the current Privy token. Re-runs whenever the viewer's wallet
 * connection state changes — flipping from disconnected to connected
 * is the most common case where a previously-failed retry suddenly
 * has an access token to work with.
 *
 * When `onlyForCompositeGridId` is set, the return value tracks the
 * per-board pending / flushing state so the lobby can render a
 * "saving metadata… keep this tab open" banner. Without that option
 * (the global sweep mounted at the top of `LobbyClient`), the return
 * value is `null` — the global sweep is fire-and-forget.
 */
export function usePendingMetadataFlush(
  options: FlushOptions = {},
): PendingMetadataFlushState | null {
  const { getAccessToken, isConnected } = useWallet();
  const { onlyForCompositeGridId } = options;
  const inFlightRef = useRef(false);
  // Local mirror of `localStorage`'s pending-set membership for the
  // targeted board. `localStorage` itself isn't reactive, so we
  // snapshot it on mount + after every flush attempt and surface it
  // via `useState`. The banner re-renders when the entry clears.
  const [isPending, setIsPending] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !onlyForCompositeGridId) return false;
    return listPendingMetadata().some(
      (p) => p.compositeGridId === onlyForCompositeGridId,
    );
  });
  const [isFlushing, setIsFlushing] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    async function sweep() {
      if (inFlightRef.current) return;
      const pending = listPendingMetadata();
      // Refresh the per-board flag every sweep so a launch that
      // mounts AFTER this hook (rare, but possible if the wizard
      // navigates and `LobbyClient` re-uses the same hook instance)
      // still sees the freshly-stashed entry.
      if (onlyForCompositeGridId) {
        setIsPending(
          pending.some((p) => p.compositeGridId === onlyForCompositeGridId),
        );
      }
      if (pending.length === 0) return;

      const targets = onlyForCompositeGridId
        ? pending.filter((p) => p.compositeGridId === onlyForCompositeGridId)
        : pending;
      if (targets.length === 0) return;

      inFlightRef.current = true;
      if (onlyForCompositeGridId) setIsFlushing(true);
      try {
        // Resolve a token once per sweep — it's the same for all
        // entries because they're owned by the currently-connected
        // wallet's session. `getAccessToken` returns null when the
        // user is disconnected; the server's local-dev path accepts
        // that, and the production path will 401 (which `postMetadata`
        // surfaces as a non-retryable error → we leave the entry).
        const accessToken = await getAccessToken().catch(() => null);

        for (const entry of targets) {
          if (cancelled) return;
          await flushOne(entry, accessToken).catch((err) => {
            console.warn(
              '[pending-metadata] flush failed for',
              entry.compositeGridId,
              err,
            );
          });
        }
      } finally {
        inFlightRef.current = false;
        if (onlyForCompositeGridId) {
          // Re-snapshot post-flush so the banner clears when the
          // POST succeeded (entry removed) or stays put when it
          // failed (entry still in place; banner stays visible so
          // the user knows the retry will try again on next mount).
          setIsPending(
            listPendingMetadata().some(
              (p) => p.compositeGridId === onlyForCompositeGridId,
            ),
          );
          setIsFlushing(false);
        }
      }
    }

    void sweep();

    return () => {
      cancelled = true;
    };
    // `isConnected` is in the dep list so a connect-after-launch flow
    // automatically retries the moment a wallet appears. Re-running on
    // every render would be wasteful; the in-flight ref guards against
    // overlapping sweeps if an effect fires twice in Strict Mode.
  }, [getAccessToken, isConnected, onlyForCompositeGridId]);

  if (!onlyForCompositeGridId) return null;
  return { isPending, isFlushing };
}
